// Maps file format extension (lowercase) → array of accepted MIME types
export const MIME_MAP = {
  jpg:  ['image/jpeg', 'image/jpg', 'jpg', 'jpeg'],
  jpeg: ['image/jpeg', 'image/jpg', 'jpeg', 'jpg'],
  png:  ['image/png'],
  svg:  ['image/svg+xml'],
  gif:  ['image/gif'],
  mp4:  ['video/mp4'],
  mov:  ['video/quicktime'],
  avi:  ['video/x-msvideo', 'video/avi', 'video/msvideo'],
  mkv:  ['video/x-matroska', 'video/matroska', 'application/octet-stream'],
  flv:  ['video/x-flv', 'video/flv', 'application/x-flv', 'application/octet-stream'],
  wmv:  ['video/x-ms-wmv', 'video/wmv'],
  '3gp':['video/3gpp', 'video/3gp', '.3gpp'],
  '3gpp':['video/3gpp', 'video/3gp', '.3gp'],
  webm: ['video/webm'],
  mp3:  ['audio/mpeg'],
  wav:  ['audio/wav', 'audio/x-wav'],
  aac:  ['audio/aac', 'audio/vnd.dlna.adts'],
  flac: ['audio/flac', 'audio/x-flac'],
  pdf:  ['application/pdf'],
  txt:  ['text/plain'],
  csv:  ['text/csv', 'text/plain'],
};

// Maps extension → media category
export const EXT_CATEGORY_MAP = {
  jpg: 'image', jpeg: 'image', png: 'image', svg: 'image', gif: 'image',
  mp4: 'video', mov: 'video', avi: 'video', mkv: 'video',
  flv: 'video', wmv: 'video', '3gp': 'video', '3gpp': 'video', webm: 'video',
  mp3: 'audio', wav: 'audio', aac: 'audio', flac: 'audio',
  pdf: 'document', txt: 'document', csv: 'document',
};

/**
 * Derive a display category ('image' | 'video' | 'audio' | 'document') from
 * a MIME type and optional filename.
 *
 * When the browser reports the generic `application/octet-stream` fallback
 * (common for AVI, FLV, WMV, MKV on Windows Chrome) we cannot trust the
 * MIME type alone.  In that case we fall back to the file extension derived
 * from `fileName`.
 */
export const getMediaCategory = (mimeType, fileName) => {
  const mime = (mimeType || '').toLowerCase();

  // Generic binary — determine from file extension instead
  if (!mime || mime === 'application/octet-stream') {
    const ext = (fileName || '').split('.').pop().toLowerCase();
    return EXT_CATEGORY_MAP[ext] ?? 'document';
  }

  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('video/')) return 'video';
  if (mime.startsWith('audio/')) return 'audio';
  return 'document';
};

export const getFileExtension = (fileName) => {
  const extension = (fileName || '').split('.').pop().toLowerCase();
  return extension;
};

/**
 * Returns true if the browser can natively play/display this format inline.
 * Formats like AVI, FLV, WMV are video files but browsers can't decode them,
 * so we show a placeholder thumbnail instead of a broken <video> element.
 */
export const isBrowserNativeVideo = (mimeType, fileName) => {
  // Formats browsers can natively play in a <video> tag
  const NATIVE_VIDEO_MIMES = new Set([
    'video/mp4', 'video/webm', 'video/ogg',
    'video/quicktime', 'video/3gpp', 'video/3gpp2',
  ]);
  const NATIVE_VIDEO_EXTS = new Set(['mp4', 'webm', 'ogv', 'ogg', 'mov', '3gp', '3gpp']);

  const mime = (mimeType || '').toLowerCase();
  const ext = (fileName || '').split('.').pop().toLowerCase();

  if (mime && mime !== 'application/octet-stream') {
    return NATIVE_VIDEO_MIMES.has(mime);
  }
  return NATIVE_VIDEO_EXTS.has(ext);
};
export const SUPPORTED_FILE_FORMATS = [
  // Image
  { ext: 'JPG',  label: 'JPG'  },
  { ext: 'JPEG', label: 'JPEG' },
  { ext: 'PNG',  label: 'PNG'  },
  { ext: 'SVG',  label: 'SVG'  },
  { ext: 'GIF',  label: 'GIF'  },
  // Video
  { ext: 'MP4',  label: 'MP4'  },
  { ext: 'MOV',  label: 'MOV'  },
  { ext: 'AVI',  label: 'AVI'  },
  { ext: 'MKV',  label: 'MKV'  },
  { ext: 'FLV',  label: 'FLV'  },
  { ext: 'WMV',  label: 'WMV'  },
  { ext: '3GP',  label: '3GP'  },
  { ext: '3GPP', label: '3GPP' },
  { ext: 'WEBM', label: 'WEBM' },
  // Audio
  { ext: 'MP3',  label: 'MP3'  },
  { ext: 'WAV',  label: 'WAV'  },
  { ext: 'AAC',  label: 'AAC'  },
  { ext: 'FLAC', label: 'FLAC' },
  // Document
  { ext: 'PDF',  label: 'PDF'  },
  { ext: 'TXT',  label: 'TXT'  },
  { ext: 'CSV',  label: 'CSV'  },
];
